/**
 * The obligation strip (docs/REMINDER_CONTRACTS_PLAN.md §12) — active
 * Reminder Contracts, bound and unbound, rendered as their own surface,
 * visually distinct from the plan's suggestion cards.
 *
 * Governed by FIDELITY, not restraint: a due contract re-surfaces every
 * time this mounts or polls; dismissal never resolves anything. The strip
 * writes no `opened` observations — rendering is system telemetry (§6).
 *
 * Actions (§7): Done resolves (bound-Done completes the item through the
 * canonical path server-side) · Snooze moves time · Acknowledge moves
 * intensity ("I know — keep holding me to it") · Stop terminates.
 *
 * Three shapes since 2026-09-12 (the phone mockup):
 *   `strip`  — the original amber card: header, "+", every contract for today.
 *   `due`    — the thin bar under /today's header: only what is held to a
 *              CLOCK today ("Take trash out · Due 18:00 · Done · Snooze").
 *              Renders nothing when nothing is due; a bar is an alert.
 *   `allday` — bare rows for /today's Reminders card: what is held to the
 *              DAY with no clock ("Check tire pressure"), the "Set a time"
 *              offer, and the "+ Add a reminder" affordance.
 * The lane a contract falls in is decided in `utils/reminderLanes.ts`, which
 * the node probe pins.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlarmClock, Check, ChevronDown, Clock3, MoreHorizontal, Plus, X,
} from 'lucide-react'
import {
  amendReminder,
  answerCheckin,
  createReminder,
  isBannerCritical,
  reminderAction,
  setReminderTime,
  type ReminderContract,
} from '../../services/reminderService'
import { useActiveReminders } from './useActiveReminders'
import { placeService, type Place } from '../../services/placeService'
import { BRAND } from '../../config/brand'
import {
  contractClock, contractDay, contractLane, dayWord, dueNow, localDayOf,
} from '../../utils/reminderLanes'

export { contractDay }

const SNOOZE_OPTIONS: Array<{ key: string; label: string }> = [
  { key: '10m', label: '10 minutes' },
  { key: '1h', label: '1 hour' },
  { key: 'this_evening', label: 'This evening' },
  { key: 'tomorrow_morning', label: 'Tomorrow morning' },
]

const INTERVALS = [
  { v: 5, label: 'every 5 min' },
  { v: 10, label: 'every 10 min' },
  { v: 15, label: 'every 15 min' },
  { v: 30, label: 'every 30 min' },
  { v: 60, label: 'hourly' },
  { v: 240, label: 'every 4 hours' },
  { v: 1440, label: 'daily' },
  { v: -1, label: 'custom…' },
]

export type ObligationStripVariant = 'strip' | 'due' | 'allday'

export function ObligationStrip({ variant = 'strip' }: { variant?: ObligationStripVariant } = {}) {
  const navigate = useNavigate()
  // 2026-08-29: was its own fetch + 60s interval. Now the shared store —
  // one request and one 45s clock for this strip and the site-wide banner
  // together (/today used to ask GET /active four times per load, and the
  // two clocks left the two copies disagreeing for up to 15s after an
  // action). `error` lets us say so instead of drawing an empty strip.
  const { contracts: allContracts, error, loaded, refresh: load } = useActiveReminders()
  // A firing critical contract already owns a pinned banner with these same
  // four actions. Fidelity wants it un-ignorable, not un-ignorable twice —
  // so the strip stands down for exactly what the banner renders, and shows
  // every other obligation.
  const todayISO = localDayOf(new Date().toISOString())
  const visible = allContracts.filter((c) => !isBannerCritical(c))
  // Today's obligations only; a later day's are shown on that day (and on
  // /schedule), not held over the person's morning (owner, 2026-09-12).
  const lanes = new Map(visible.map((c) => [c.id, contractLane(c, todayISO)] as const))
  const contracts = visible.filter((c) => {
    const lane = lanes.get(c.id)
    if (variant === 'due') return lane === 'due'
    if (variant === 'allday') return lane === 'allday'
    return lane !== 'later'
  })
  const laterCount = visible.filter((c) => lanes.get(c.id) === 'later').length
  const compact = variant === 'due'
  const [busyId, setBusyId] = useState<number | null>(null)
  const [snoozeFor, setSnoozeFor] = useState<number | null>(null)
  const [amendFor, setAmendFor] = useState<number | null>(null)
  // "Set a time" for a contract said in chat with no when (2026-09-12).
  const [timeFor, setTimeFor] = useState<number | null>(null)
  const [timeDate, setTimeDate] = useState('')
  const [timeClock, setTimeClock] = useState('')
  const [creating, setCreating] = useState(false)
  const [text, setText] = useState('')
  const [interval, setIntervalMin] = useState(30)
  const [customMin, setCustomMin] = useState('')
  const [critical, setCritical] = useState(false)
  // Phase 4 location trigger: 0 = "now" (time trigger); a place id arms
  // the nag on arrival instead.
  const [places, setPlaces] = useState<Place[]>([])
  const [placeId, setPlaceId] = useState(0)

  useEffect(() => {
    if (!creating) return
    placeService.list().then(setPlaces).catch(() => undefined)
  }, [creating])

  const run = async (
    c: ReminderContract,
    action: 'done' | 'snooze' | 'acknowledge' | 'stop',
    target?: string,
  ) => {
    setBusyId(c.id)
    try {
      await reminderAction(c.id, action, target)
      setSnoozeFor(null)
      await load()
    } finally {
      setBusyId(null)
    }
  }

  const submitCreate = async () => {
    if (!text.trim()) return
    const minutes = interval === -1
      ? Math.max(5, parseInt(customMin, 10) || 30)
      : interval
    setBusyId(-1)
    try {
      await createReminder({
        obligation_text: text.trim(),
        interval_minutes: minutes,
        critical,
        ...(placeId > 0
          ? { trigger: { kind: 'location', place_id: placeId, on: 'enter' } }
          : {}),
      })
      setText('')
      setCritical(false)
      setPlaceId(0)
      setCreating(false)
      await load()
    } finally {
      setBusyId(null)
    }
  }

  // The due bar is an alert: it exists only while something is on a clock
  // today. Creating and empty states belong to the Reminders card.
  if (variant === 'due' && contracts.length === 0) return null

  if (contracts.length === 0 && !creating && error) {
    // 2026-08-29: a failed fetch used to fall through to the "nothing is
    // holding you" affordance below — the strip claimed the user had no
    // obligations when it had simply not been able to ask. Say which.
    return (
      <div
        role="status"
        className="flex w-full items-center gap-2.5 rounded-2xl border border-warn/40 bg-warn/5 px-4 py-3 text-left text-sm"
      >
        <AlarmClock className="h-4 w-4 shrink-0 text-warn" />
        <span className="min-w-0 flex-1 text-secondary">
          <span className="font-semibold text-primary">
            Couldn't reach your reminders just now.
          </span>
          <span className="text-xs text-muted"> — anything you asked to be held to is still running.</span>
        </span>
        <button
          type="button"
          onClick={() => void load()}
          className="min-h-touch shrink-0 rounded-lg border border-warn/40 bg-surface px-3 text-xs font-medium text-warn hover:bg-warn/10"
        >
          Try again
        </button>
      </div>
    )
  }

  if (contracts.length === 0 && !creating && !loaded) return null

  const laterLink = laterCount > 0 && (
    <button
      type="button"
      onClick={() => navigate('/schedule')}
      className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-brand hover:bg-surface-hover"
    >
      {laterCount === 1 ? '1 on a later day' : `${laterCount} on later days`} · Schedule
    </button>
  )

  const addButton = (
    <button
      type="button"
      onClick={() => setCreating((v) => !v)}
      className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-secondary hover:text-primary hover:bg-surface-hover transition-colors"
    >
      {creating ? <X className="h-3.5 w-3.5 text-warn" /> : <Plus className="h-3.5 w-3.5 text-warn" />}
      {creating ? 'Cancel' : 'Add a reminder'}
    </button>
  )

  const createForm = creating && (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') void submitCreate() }}
        placeholder="What should I not let you forget?"
        className="min-h-touch flex-1 min-w-[200px] rounded-xl border border-soft bg-surface px-3 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-warn/40"
      />
      <select
        value={interval}
        onChange={(e) => setIntervalMin(Number(e.target.value))}
        aria-label="Reminder cadence"
        className="min-h-touch rounded-xl border border-soft bg-surface px-2 text-sm text-secondary"
      >
        {INTERVALS.map((i) => (
          <option key={i.v} value={i.v}>{i.label}</option>
        ))}
      </select>
      {interval === -1 && (
        <input
          type="number"
          min={5}
          value={customMin}
          onChange={(e) => setCustomMin(e.target.value)}
          placeholder="min"
          aria-label="Custom cadence in minutes"
          className="min-h-touch w-20 rounded-xl border border-soft bg-surface px-2 text-sm text-secondary"
        />
      )}
      <label className="flex min-h-touch cursor-pointer items-center gap-1.5 rounded-xl border border-soft bg-surface px-3 text-xs text-secondary hover:border-bad/50">
        <input
          type="checkbox"
          checked={critical}
          onChange={(e) => setCritical(e.target.checked)}
          className="accent-[var(--semantic-error)]"
        />
        critical
        <span className="hidden md:inline text-app-micro text-muted">— banner on every screen</span>
      </label>
      {places.length > 0 && (
        <select
          value={placeId}
          onChange={(e) => setPlaceId(Number(e.target.value))}
          aria-label="Start reminding when I arrive at"
          className="min-h-touch rounded-xl border border-soft bg-surface px-2 text-sm text-secondary"
        >
          <option value={0}>starting now</option>
          {places.map((p) => (
            <option key={p.id} value={p.id}>when I arrive at {p.title}</option>
          ))}
        </select>
      )}
      <button
        type="button"
        disabled={!text.trim() || busyId === -1}
        onClick={() => void submitCreate()}
        className="min-h-touch rounded-xl bg-warn px-4 text-sm font-bold text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
      >
        Hold me to it
      </button>
    </div>
  )

  if (contracts.length === 0 && !creating) {
    // Empty state stays quiet relative to the amber strip, but must be
    // FINDABLE — the owner couldn't locate reminders at all when this was
    // a bare text link (2026-08-25). Quieted 2026-08-30: the loud dashed
    // row was the FIRST thing on the page on an ordinary day. It is the
    // empty state of a card that names itself, so it only needs to offer
    // the action — and to say where the later ones went.
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs text-secondary">
        {variant === 'allday'
          ? <span>Nothing held for today.</span>
          : laterCount > 0 && <span>Nothing to hold you to today.</span>}
        {laterLink}
        {addButton}
      </div>
    )
  }

  const renderRow = (c: ReminderContract) => {
    const due = dueNow(c) || c.op_state === 'firing'
    const clock = contractClock(c)
    const waitingForTime = c.op_state === 'waiting' && !c.next_fire_at && c.awaiting_reason === 'time'
    const namedDay = c.bound_due_date || c.stated_due_date
    const openItem = () => {
      if (!c.bound_item_id) return
      if (c.bound_item_type === 'task') navigate(`/tasks/${c.bound_item_id}`)
      if (c.bound_item_type === 'habit_definition') navigate(`/habits/${c.bound_item_id}`)
    }
    const doneButton = (
      <button
        type="button"
        disabled={busyId === c.id}
        onClick={() => void run(c, 'done')}
        className="min-h-touch rounded-lg border border-ok bg-surface px-3 text-xs font-semibold text-ok hover:bg-ok/10 disabled:opacity-50 inline-flex items-center gap-1.5"
      >
        <Check className="h-3.5 w-3.5" /> Done
      </button>
    )
    // Snooze moves a clock. A contract with no clock has nothing to move;
    // it gets "Set a time" instead.
    const snoozeButton = (c.next_fire_at || c.deadline_at) && (
      <div className="relative">
        <button
          type="button"
          disabled={busyId === c.id}
          onClick={() => setSnoozeFor(snoozeFor === c.id ? null : c.id)}
          className="min-h-touch rounded-lg border border-soft bg-surface px-3 text-xs text-secondary hover:bg-surface-alt disabled:opacity-50 inline-flex items-center gap-1"
        >
          <Clock3 className="h-3.5 w-3.5" /> Snooze <ChevronDown className="h-3 w-3" />
        </button>
        {snoozeFor === c.id && (
          <div className="absolute z-30 mt-1 w-44 rounded-xl border border-soft bg-surface p-1 shadow-lg">
            {SNOOZE_OPTIONS.map((o) => (
              <button
                key={o.key}
                type="button"
                onClick={() => void run(c, 'snooze', o.key)}
                className="block w-full rounded-lg px-3 py-2 text-left text-xs text-primary hover:bg-surface-alt"
              >
                {o.label}
              </button>
            ))}
          </div>
        )}
      </div>
    )
    const moreMenu = (
      <div className={`relative ${compact ? '' : 'ml-auto'}`}>
        <button
          type="button"
          aria-label="Adjust reminder"
          onClick={() => setAmendFor(amendFor === c.id ? null : c.id)}
          className="inline-flex min-h-touch min-w-touch items-center justify-center rounded-lg px-2 text-muted hover:text-secondary hover:bg-surface-alt transition-colors"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
        {amendFor === c.id && (
          <div className="absolute right-0 z-30 mt-1 w-48 rounded-xl border border-soft bg-surface p-1 shadow-lg">
            <button
              type="button"
              disabled={busyId === c.id}
              onClick={async () => { setAmendFor(null); await run(c, 'acknowledge') }}
              className="block w-full rounded-lg px-3 py-2 text-left text-xs text-primary hover:bg-surface-alt disabled:opacity-50"
            >
              Got it — keep reminding me
            </button>
            <div className="my-1 border-t border-soft" />
            <div className="px-3 py-1 text-app-micro font-semibold text-muted">How often</div>
            {INTERVALS.map((i) => (
              <button
                key={i.v}
                type="button"
                onClick={async () => { await amendReminder(c.id, { interval_minutes: i.v }); setAmendFor(null); await load() }}
                className={`block w-full rounded-lg px-3 py-1.5 text-left text-xs hover:bg-surface-alt ${c.interval_minutes === i.v ? 'text-brand font-bold' : 'text-primary'}`}
              >
                {i.label}
              </button>
            ))}
            <div className="px-3 py-1 text-app-micro font-semibold text-muted">How insistent</div>
            {(['normal', 'persistent', 'aggressive'] as const).map((lvl) => (
              <button
                key={lvl}
                type="button"
                onClick={async () => { await amendReminder(c.id, { persistence: lvl }); setAmendFor(null); await load() }}
                className={`block w-full rounded-lg px-3 py-1.5 text-left text-xs capitalize hover:bg-surface-alt ${c.persistence === lvl ? 'text-brand font-bold' : 'text-primary'}`}
              >
                {lvl}
              </button>
            ))}
            {compact && (
              <>
                <div className="my-1 border-t border-soft" />
                <button
                  type="button"
                  disabled={busyId === c.id}
                  onClick={async () => { setAmendFor(null); await run(c, 'stop') }}
                  className="block w-full rounded-lg px-3 py-2 text-left text-xs text-muted hover:text-bad hover:bg-bad/10 disabled:opacity-50"
                  title="Stop reminding — ends this contract"
                >
                  Stop reminding
                </button>
              </>
            )}
          </div>
        )}
      </div>
    )
    const stopButton = !compact && (
      <button
        type="button"
        disabled={busyId === c.id}
        onClick={() => void run(c, 'stop')}
        className="min-h-touch rounded-lg px-2.5 text-xs text-muted hover:text-bad hover:bg-bad/10 disabled:opacity-50 transition-colors"
        title="Stop reminding — ends this contract"
      >
        Stop
      </button>
    )
    const chips = (
      <>
        {c.critical && (
          <span className="px-2 py-0.5 rounded-full bg-bad/15 text-bad text-app-micro font-bold uppercase">
            critical
          </span>
        )}
        {c.overdue && (
          // 2026-08-29: was "overdue" — deficit vocabulary narrated
          // at the user. State the fact about the obligation instead.
          <span className="px-2 py-0.5 rounded-full bg-warn/15 text-warn text-app-micro font-bold"
                title="Past the time you set — still holding you to it.">
            still open
          </span>
        )}
        {waitingForTime && (
          // 2026-09-12: a chat ask with no when used to start nagging in
          // thirty minutes. It waits now, and says so — the time comes
          // from the task once it has one. Bound to a task that HAS a
          // day, it says the day (the task is the one truth).
          <span className="px-2 py-0.5 rounded-full bg-surface-alt text-muted text-app-micro"
                title="Held, not yet timed. It gets its time from the task once that has one, or from you.">
            {namedDay
              ? `${dayWord(String(namedDay).slice(0, 10), todayISO)} · no time yet`
              : 'waiting for a time'}
          </span>
        )}
        {waitingForTime && (
          <button
            type="button"
            onClick={() => {
              // The day they already named is the form's default day.
              const named = namedDay ? String(namedDay).slice(0, 10) : null
              setTimeDate(timeFor === c.id ? '' : (named ?? todayISO))
              setTimeClock('')
              setTimeFor(timeFor === c.id ? null : c.id)
            }}
            className="min-h-touch rounded-lg border border-brand-subtle-border bg-surface px-2.5 text-app-micro font-semibold text-brand hover:bg-brand-subtle"
          >
            Set a time
          </button>
        )}
        {clock?.kind === 'snoozed' && (
          <span className="px-2 py-0.5 rounded-full bg-surface-alt text-muted text-app-micro tabular-nums">
            snoozed until {clock.clock}
          </span>
        )}
        {c.binding_broken_at && (
          <span className="px-2 py-0.5 rounded-full bg-warn/15 text-warn text-app-micro font-medium"
                title="The item this was bound to is gone — keep as a plain reminder, or stop it.">
            item gone
          </span>
        )}
      </>
    )
    const subBlocks = (
      <>
        {timeFor === c.id && (
          <form
            className="mt-2 flex flex-wrap items-center gap-2 rounded-xl bg-surface-alt px-3 py-2"
            onSubmit={async (e) => {
              e.preventDefault()
              if (!timeDate || !timeClock) return
              setBusyId(c.id)
              try {
                const ok = await setReminderTime(c.id, timeDate, timeClock)
                if (ok) { setTimeFor(null); await load() }
              } finally {
                setBusyId(null)
              }
            }}
          >
            <label className="text-xs text-secondary">When?</label>
            <input
              type="date"
              value={timeDate}
              onChange={(e) => setTimeDate(e.target.value)}
              className="min-h-touch rounded-lg border border-soft bg-surface px-2 text-xs text-primary"
              aria-label="Day"
              required
            />
            <input
              type="time"
              value={timeClock}
              onChange={(e) => setTimeClock(e.target.value)}
              className="min-h-touch rounded-lg border border-soft bg-surface px-2 text-xs text-primary"
              aria-label="Time"
              required
            />
            <button
              type="submit"
              disabled={busyId === c.id || !timeDate || !timeClock}
              className="min-h-touch rounded-lg bg-brand px-3 text-xs font-semibold text-on-brand hover:bg-brand-hover disabled:opacity-50"
            >
              Hold me to it
            </button>
            <span className="text-app-micro text-muted">
              {c.bound_item_id ? 'Sets the task’s due time too.' : 'Your local time.'}
            </span>
          </form>
        )}
        {c.quiet_suppressed && (
          <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl bg-surface-alt px-3 py-2">
            <p className="text-xs text-secondary">
              {BRAND.assistant} is honoring your quiet hours — holding this
              {c.quiet_resume_at
                ? ` until ${new Date(c.quiet_resume_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
                : ' until morning'}.
            </p>
            <button
              type="button"
              onClick={async () => { await reminderAction(c.id, 'alert_anyway', undefined, { override_minutes: 120 }); await load() }}
              className="min-h-touch rounded-lg border border-warn/40 bg-surface px-2.5 text-app-micro font-medium text-warn hover:bg-warn/10"
            >
              No — keep alerting me (2h)
            </button>
            <button
              type="button"
              onClick={async () => { await reminderAction(c.id, 'alert_anyway', undefined, { override_minutes: 480 }); await load() }}
              className="min-h-touch rounded-lg px-2 text-app-micro text-muted hover:text-warn"
            >
              all night
            </button>
          </div>
        )}
        {c.quiet_override_until && new Date(c.quiet_override_until).getTime() > Date.now() && (
          <p className="mt-1.5 text-app-micro text-warn">
            alerting through quiet hours until {new Date(c.quiet_override_until).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
          </p>
        )}
        {c.pending_checkin_id != null && (
          <div className="mt-2 rounded-xl bg-brand-subtle/60 border border-brand-subtle-border px-3 py-2">
            <p className="text-xs text-secondary">
              {/* 2026-08-29: "overdue a while" narrated a deficit at
                  the user; ask about the obligation, not their record. */}
              {c.overdue
                ? "This one's been open a while. Keep holding you to it?"
                : `I've been holding you to this for a while. Still want me to?`}
            </p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={async () => { await answerCheckin(c.id, 'keep'); await load() }}
                className="min-h-touch rounded-lg border border-brand-subtle-border bg-surface px-3 text-xs font-semibold text-brand hover:bg-brand-subtle"
              >
                Keep nagging
              </button>
              <button
                type="button"
                onClick={async () => { await answerCheckin(c.id, 'reschedule'); await load() }}
                className="min-h-touch rounded-lg border border-brand-subtle-border bg-surface px-3 text-xs text-secondary hover:bg-surface-alt"
              >
                Tomorrow morning
              </button>
              <button
                type="button"
                onClick={async () => { await answerCheckin(c.id, 'no_longer_possible'); await load() }}
                className="min-h-touch rounded-lg px-3 text-xs text-muted hover:text-bad hover:bg-bad/10"
              >
                No longer possible
              </button>
            </div>
          </div>
        )}
      </>
    )

    if (compact) {
      // One line: bell · what · when · Done · Snooze · ⋯ (the mockup's bar).
      // The clock says what it IS — due, next reminder, or snooze end — so a
      // bare "18:00" never has to be interpreted.
      const when = clock
        ? clock.kind === 'due' ? `Due ${clock.clock}`
        : clock.kind === 'next' ? `Reminds at ${clock.clock}`
        : null
        : null
      return (
        <div key={c.id} data-testid="due-row" className={due ? 'rounded-xl bg-surface px-2 py-1' : 'px-2 py-1'}>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <AlarmClock className={`h-4 w-4 shrink-0 ${due ? 'text-warn' : 'text-muted'}`} aria-hidden="true" />
            <button
              type="button"
              onClick={openItem}
              disabled={!c.bound_item_id}
              className={`min-w-0 flex-1 text-left text-app-body font-semibold text-primary truncate ${c.bound_item_id ? 'hover:underline' : ''}`}
            >
              {c.obligation_text}
            </button>
            {when && <span className="text-app-meta text-secondary tabular-nums shrink-0">{when}</span>}
            {chips}
            <span className="ml-auto flex items-center gap-1.5">
              {doneButton}
              {snoozeButton}
              {moreMenu}
            </span>
          </div>
          {subBlocks}
        </div>
      )
    }

    return (
      <div
        key={c.id}
        className={`rounded-xl border p-3 transition-colors ${
          due
            ? 'border-warn/50 bg-surface shadow-[0_2px_10px_rgba(166,124,60,0.15)]'
            : 'border-soft bg-surface/70'
        }`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <p
            className={`min-w-0 flex-1 text-sm font-semibold text-primary ${
              c.bound_item_id ? 'cursor-pointer hover:underline' : ''
            }`}
            style={{ fontFamily: 'var(--font-display)' }}
            onClick={openItem}
          >
            {c.obligation_text}
          </p>
          {chips}
        </div>
        {subBlocks}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {doneButton}
          {snoozeButton}
          {moreMenu}
          {stopButton}
        </div>
      </div>
    )
  }

  if (variant === 'due') {
    return (
      <div
        role="region"
        aria-label="Due today"
        data-testid="due-bar"
        className="rounded-2xl border border-tint-warn-edge bg-tint-warn-soft px-2 py-1.5 space-y-1"
      >
        {error && (
          <p className="px-2 text-app-micro text-muted">
            Showing the last answer — couldn't reach your reminders just now.
          </p>
        )}
        {contracts.map(renderRow)}
      </div>
    )
  }

  if (variant === 'allday') {
    return (
      <div data-testid="allday-reminders">
        {error && (
          <p className="mb-2 text-app-micro text-muted">
            Showing the last answer — couldn't reach your reminders just now.
          </p>
        )}
        <div className="space-y-2">{contracts.map(renderRow)}</div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {laterLink}
          {addButton}
        </div>
        {createForm}
      </div>
    )
  }

  return (
    <div className="rounded-2xl border border-warn/40 bg-warn/5 p-4 shadow-sm">
      <div className="flex items-center gap-2 text-warn">
        <AlarmClock className="h-4 w-4" />
        <span className="text-app-meta font-semibold">
          Holding you to {contracts.length === 1 ? 'one thing' : `${contracts.length} things`}
        </span>
        {/* 2026-08-29: icon-only button was 28px square — under the 44px
            touch floor in BOTH dimensions, not just height. */}
        <button
          type="button"
          onClick={() => setCreating((v) => !v)}
          aria-label="New reminder"
          className="ml-auto inline-flex min-h-touch min-w-touch items-center justify-center rounded-lg text-warn hover:bg-warn/10 transition-colors"
        >
          {creating ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </button>
      </div>
      {error && (
        // 2026-08-29: stale-but-shown is fine; pretending it's fresh is not.
        <p className="mt-1 text-app-micro text-muted">
          Showing the last answer — couldn't reach your reminders just now.
        </p>
      )}

      {createForm}

      {laterCount > 0 && (
        <button
          type="button"
          onClick={() => navigate('/schedule')}
          className="mt-1 inline-flex items-center gap-1 rounded-lg px-1.5 py-0.5 -ml-1.5 text-app-micro text-muted hover:text-secondary hover:bg-surface-hover"
        >
          {laterCount === 1 ? '1 more on a later day' : `${laterCount} more on later days`} · Schedule
        </button>
      )}
      <div className="mt-3 space-y-2">
        {contracts.map(renderRow)}
      </div>
    </div>
  )
}
